// Centralized query keys for React Query
// Following best practices from https://tkdodo.eu/blog/effective-react-query-keys

export const queryKeys = {
  // Accounts
  accounts: {
    all: ['accounts'] as const,
    list: () => [...queryKeys.accounts.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.accounts.all, 'detail', id] as const,
  },

  // Transactions
  transactions: {
    all: ['transactions'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.transactions.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.transactions.all, 'detail', id] as const,
    byAccount: (accountId: string) => [...queryKeys.transactions.all, 'byAccount', accountId] as const,
  },

  // Categories
  categories: {
    all: ['categories'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.categories.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.categories.all, 'detail', id] as const,
    stats: (month: string) => [...queryKeys.categories.all, 'stats', month] as const,
  },

  // Budgets
  budgets: {
    all: ['budgets'] as const,
    list: () => [...queryKeys.budgets.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.budgets.all, 'detail', id] as const,
    progress: (id: string) => [...queryKeys.budgets.all, 'progress', id] as const,
  },

  // Debts
  debts: {
    all: ['debts'] as const,
    list: () => [...queryKeys.debts.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.debts.all, 'detail', id] as const,
    payments: (debtId: string) => [...queryKeys.debts.all, 'payments', debtId] as const,
    summary: () => [...queryKeys.debts.all, 'summary'] as const,
    projection: (debtId: string, extraPayment?: number) =>
      [...queryKeys.debts.all, 'projection', debtId, extraPayment] as const,
    snowball: () => [...queryKeys.debts.all, 'snowball'] as const,
    avalanche: () => [...queryKeys.debts.all, 'avalanche'] as const,
    detailEnhanced: (id: string) => [...queryKeys.debts.all, 'detailEnhanced', id] as const,
    forecast: (id: string, extraMonthly?: number) =>
      [...queryKeys.debts.all, 'forecast', id, extraMonthly] as const,
    rateHistory: (id: string) => [...queryKeys.debts.all, 'rateHistory', id] as const,
    mortgageBundle: (mortgageId: string) => [...queryKeys.debts.all, 'mortgageBundle', mortgageId] as const,
  },

  // Recurring
  recurring: {
    all: ['recurring'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.recurring.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.recurring.all, 'detail', id] as const,
  },

  // Bank Connections
  bankConnections: {
    all: ['bankConnections'] as const,
    list: () => [...queryKeys.bankConnections.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.bankConnections.all, 'detail', id] as const,
  },

  // Bank Institutions
  bankInstitutions: {
    all: ['bankInstitutions'] as const,
    list: (country?: string) =>
      [...queryKeys.bankInstitutions.all, 'list', { country }] as const,
  },

  // Categorization
  categorization: {
    all: ['categorization'] as const,
    suggest: (description: string) =>
      [...queryKeys.categorization.all, 'suggest', description] as const,
  },

  // Rules
  rules: {
    all: ['rules'] as const,
    list: () => [...queryKeys.rules.all, 'list'] as const,
  },

  // Savings Goals
  savings: {
    all: ['savings'] as const,
    list: () => [...queryKeys.savings.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.savings.all, 'detail', id] as const,
    summary: () => [...queryKeys.savings.all, 'summary'] as const,
    contributions: (goalId: string) => [...queryKeys.savings.all, 'contributions', goalId] as const,
  },

  // Import Templates
  importTemplates: {
    all: ['importTemplates'] as const,
    list: (accountId?: string) => ['importTemplates', 'list', accountId] as const,
  },

  // Exchange Rates
  exchangeRates: {
    all: ['exchangeRates'] as const,
    convert: (from: string, to: string) =>
      [...queryKeys.exchangeRates.all, from, to] as const,
  },

  // Config
  config: {
    all: ['config'] as const,
    locale: () => [...queryKeys.config.all, 'locale'] as const,
    features: () => [...queryKeys.config.all, 'features'] as const,
    enums: () => [...queryKeys.config.all, 'enums'] as const,
    banks: () => [...queryKeys.config.all, 'banks'] as const,
  },
};

// Helper to get all keys for a resource (useful for invalidation)
export const getResourceKeys = (resource: keyof typeof queryKeys) => {
  return queryKeys[resource].all;
};
